// AdCard.tsx - Mimics EventCard but for advertisements

import { useState } from 'react';
import { Card, Image, Text, Badge, Group, Box, Title, ActionIcon, Skeleton } from '@mantine/core';
import { IconExternalLink, IconAd } from '@tabler/icons-react';

interface Ad {
  id: number;
  title: string;
  description?: string;
  url: string;
  banner_url?: string;
  ad_type: string;
  type: 'ad';
}

interface AdCardProps {
  ad: Ad;
  onAdClick?: (ad: Ad) => void;
}

export default function AdCard({ ad, onAdClick }: AdCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);

  // Handle card click
  const handleCardClick = (e: React.MouseEvent) => {
    // If clicking on a link or button inside the card, don't trigger the handler
    if ((e.target as HTMLElement).closest('a') || (e.target as HTMLElement).closest('button')) {
      return;
    }

    // Prevent default behavior
    e.preventDefault();
    e.stopPropagation();

    // Track the click with the backend
    trackAdClick(ad.id);

    // Open ad URL in new tab
    if (ad.url) {
      window.open(ad.url, '_blank', 'noopener,noreferrer');
    }

    // Call the parent's onAdClick if provided
    if (onAdClick) {
      onAdClick(ad);
    }
  };

  // Track ad click with backend
  const trackAdClick = async (adId: number) => {
    try {
      await fetch(`/ads/${adId}/track_click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      });
    } catch (error) {
      console.error('Failed to track ad click:', error);
    }
  };

  return (
    <Card
      shadow="sm"
      padding={0}
      radius="md"
      withBorder
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        backgroundColor: '#fff7ed1a',
        border: '2px solid #EA580C40', // Orange border to distinguish ads
      }}
      onClick={handleCardClick}
    >
      {/* Image Section with Sponsored Badge */}
      <Card.Section style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
        {!imageLoaded && <Skeleton height={200} radius="md" animate={true} />}

        {/* Ad Image */}
        <Image
          src={ad.banner_url || 'https://placehold.co/600x400/EA580C/FFFFFF?text=Advertisement'}
          alt={ad.title}
          placeholder={<Skeleton height="100%" radius="md" animate />}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          onLoad={() => setImageLoaded(true)}
        />

        {/* Sponsored Badge - Upper Left Corner */}
        <Group
          style={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 10,
            gap: '0.3rem',
          }}
        >
          <Badge variant="filled" color="#EA580C" size="sm" className="poppins" leftSection={<IconAd size={12} />}>
            Sponsored
          </Badge>
        </Group>

        {/* External Link Icon - Lower Right Corner */}
        <ActionIcon
          variant="transparent"
          style={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            width: 35,
            height: 35,
            zIndex: 10,
            background: 'rgba(234, 88, 12, 0.8)',
            borderRadius: '50%',
          }}
          onClick={(e) => {
            e.stopPropagation();
            trackAdClick(ad.id);
            window.open(ad.url, '_blank', 'noopener,noreferrer');
          }}
        >
          <IconExternalLink
            size={20}
            style={{
              color: 'white',
            }}
          />
        </ActionIcon>
      </Card.Section>

      {/* Info Section */}
      <Box
        px="lg"
        py="md"
        style={{
          backgroundColor: 'rgba(28, 28, 28, 0.95)',
          color: 'white',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #EA580C30',
        }}
      >
        {/* Sponsored indicator */}
        <Group mb="xs" gap="4px">
          <IconAd size={14} style={{ color: '#EA580C' }} />
          <Text
            size="xs"
            className="poppins"
            style={{
              color: '#EA580C',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Sponsored Content
          </Text>
        </Group>

        <Title
          order={4}
          lineClamp={2}
          className="rubik my-1"
          style={{
            color: '#FFF7ED',
            lineHeight: 1.2,
          }}
        >
          {ad.title}
        </Title>

        {ad.description && (
          <Text
            size="sm"
            lineClamp={3}
            style={{
              color: '#b8b8b8',
              lineHeight: 1.3,
              fontSize: '12px',
            }}
            className="poppins mt-2"
          >
            {ad.description}
          </Text>
        )}

        {/* CTA Section */}
        <Group mt="auto" pt="sm" justify="space-between" align="center">
          <Text
            size="xs"
            style={{
              color: '#EA580C',
              fontWeight: 500,
            }}
            className="poppins"
          >
            Click to learn more
          </Text>
          <ActionIcon variant="filled" color="#EA580C" size="sm" radius="xl">
            <IconExternalLink size={12} />
          </ActionIcon>
        </Group>
      </Box>
    </Card>
  );
}
